import { XIcon } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  combineDateTimeParts,
  splitDateTimeISO,
  type TimeParts,
} from "@/shared/lib/datetime-picker";
import { DatePicker } from "@/shared/ui/DatePicker";
import { TimePicker } from "@/shared/ui/TimePicker";
import { cn } from "@/shared/lib/utils";

type CaptionLayout =
  | "label"
  | "dropdown"
  | "dropdown-months"
  | "dropdown-years"
  | undefined;

export type DateTimePickerProps = {
  value?: string | null;
  onChange?: (iso: string | null) => void;
  datePlaceholder?: string;
  timePlaceholder?: string;
  disabled?: boolean;
  className?: string;
  clearable?: boolean;
  captionLayout?: CaptionLayout;
  fromYear?: number;
  toYear?: number;
  defaultTime?: TimeParts;
  minuteStep?: number;
};

export function DateTimePicker({
  value,
  onChange,
  datePlaceholder = "ເລືອກວັນທີ",
  timePlaceholder = "ເລືອກເວລາ",
  disabled,
  className,
  clearable = false,
  captionLayout,
  fromYear,
  toYear,
  defaultTime = { hour: 8, minute: 0 },
  minuteStep,
}: DateTimePickerProps) {
  const { date, time } = splitDateTimeISO(value ?? null);

  const emit = (
    nextDate: Date | null,
    nextTime: TimeParts | null,
  ) => {
    if (!nextDate && !nextTime) {
      onChange?.(null);
      return;
    }
    const resolvedDate =
      nextDate ??
      (nextTime
        ? new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())
        : null);
    const resolvedTime = nextTime ?? defaultTime;
    onChange?.(combineDateTimeParts(resolvedDate, resolvedTime));
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <DatePicker
        mode="single"
        value={date}
        onChange={(nextDate) => emit(nextDate, time ?? defaultTime)}
        placeholder={datePlaceholder}
        disabled={disabled}
        className="min-w-0 flex-1"
        captionLayout={captionLayout}
        fromYear={fromYear}
        toYear={toYear}
      />
      <TimePicker
        value={time}
        onChange={(nextTime) => {
          if (!nextTime) {
            if (clearable && !date) onChange?.(null);
            else if (date) emit(date, defaultTime);
            return;
          }
          emit(date, nextTime);
        }}
        placeholder={timePlaceholder}
        disabled={disabled}
        className="w-34 shrink-0"
        minuteStep={minuteStep}
        allowClear={clearable}
      />
      {clearable && value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-9 shrink-0"
          disabled={disabled}
          aria-label="ລ້າງວັນທີເວລາ"
          onClick={() => onChange?.(null)}
        >
          <XIcon className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}
